import React, { useEffect, useState } from 'react';
import APIService from '../services/api';
import { BadgeJackpot } from './SourceurV2Suivi';

/** Le profil ÉTALON : « le client dit que ce profil est top, je veux le
 *  rencontrer ». Le moteur le lit en entier et rend un dossier — résumé,
 *  points forts prouvés, compétences clés, mots rares, séniorité calculée,
 *  secteurs, langues, signaux, points à vérifier, critères du clone — puis la
 *  recherche booléenne qui trouvera ses CLONES. Tout vient du serveur ; rien
 *  n'est estimé ici.
 *
 *  cible : { candidate_id } | { task_id, recruiter_id } | { texte }, + nom, titre.
 *  besoin : la campagne de clones lui sera rattachée.
 *  onUtiliser(etalon) : reprendre le dossier dans le formulaire de lancement.
 *  onLance(reponse) : la campagne de clones est partie.
 */
const BASE = 'https://besoins-tat-production.up.railway.app';
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const mono = { fontFamily: "'Space Mono','IBM Plex Mono',monospace" };

/** Le dossier en texte — ce que reprend le formulaire « profil type ». */
export function texteDossier(d = {}) {
  const l = (a) => (Array.isArray(a) ? a.filter(Boolean).join(', ') : '');
  return [
    d.titre ? `Profil : ${d.titre}` : '',
    d.resume ? `Résumé : ${d.resume}` : '',
    l(d.criteres_clone) ? `Critères du clone : ${l(d.criteres_clone)}` : '',
    l(d.competences_cles) ? `Compétences clés : ${l(d.competences_cles)}` : '',
    l(d.rares) ? `Mots rares : ${l(d.rares)}` : '',
    d.seniorite ? `Séniorité : ${d.seniorite.niveau || ''}${d.seniorite.annees != null ? ` (~${d.seniorite.annees} ans)` : ''}` : '',
    l(d.secteurs) ? `Secteurs : ${l(d.secteurs)}` : '',
    l(d.langues) ? `Langues : ${l(d.langues)}` : '',
    d.localisation ? `Localisation : ${d.localisation}` : '',
  ].filter(Boolean).join('\n');
}

const NIVEAU = { junior: 'Junior', confirme: 'Confirmé', senior: 'Senior', expert: 'Expert', direction: 'Direction' };
const villeUtile = (v) => (v && !/non pr[eé]cis|inconnu/i.test(v) ? v : '');

export default function ProfilEtalon({ cible, besoin, onFermer, onUtiliser, onLance, showNotification }) {
  const [etat, setEtat] = useState({ statut: 'lecture', message: '' }); // lecture | pret | erreur | absent
  const [etalon, setEtalon] = useState(null);
  const [query, setQuery] = useState('');
  const [ville, setVille] = useState(String(besoin?.localisation || besoin?.ville || '').split(/[,;/|]/)[0].trim()); // la ville du besoin par défaut
  const [jackpot, setJackpot] = useState('');
  const [topN, setTopN] = useState(5);
  const [nombre, setNombre] = useState(1); // l'objectif : combien de profils comme lui
  const [otwSeul, setOtwSeul] = useState(false); // ne retenir que les clones « open to work »
  const [v2, setV2] = useState(true);
  const [lancement, setLancement] = useState(false);
  const cle = JSON.stringify(cible || {});

  // Échap referme le tiroir.
  useEffect(() => {
    const surTouche = (e) => { if (e.key === 'Escape') onFermer?.(); };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [onFermer]);

  // L'analyse part à l'ouverture : une vingtaine de secondes, le moteur lit
  // le profil en entier puis dérive la recherche de ses clones.
  useEffect(() => {
    let annule = false;
    (async () => {
      setEtat({ statut: 'lecture', message: '' }); setEtalon(null);
      try {
        const c = JSON.parse(cle);
        const r = await fetch(`${BASE}/sourcing/etalon`, { method: 'POST', headers: jsonH(), body: JSON.stringify({
          candidate_id: c.candidate_id ?? undefined, task_id: c.task_id ?? undefined, recruiter_id: c.recruiter_id ?? undefined,
          texte: c.texte ?? undefined, nom: c.nom ?? undefined, poste: c.poste ?? undefined }) });
        const j = await r.json().catch(() => ({}));
        if (annule) return;
        if (r.status === 404 || r.status === 405 || r.status === 501) { setEtat({ statut: 'absent', message: j.error || '' }); return; }
        if (!r.ok || j.success === false || !j.etalon) { setEtat({ statut: 'erreur', message: j.error || `Analyse impossible (HTTP ${r.status}).` }); return; }
        const e = j.etalon;
        setEtalon(e);
        setQuery(e.clone?.query || '');
        setVille(villeUtile(e.clone?.ville) || villeUtile(e.dossier?.localisation) || '');
        setJackpot(Array.isArray(e.dossier?.rares) ? e.dossier.rares.join(', ') : '');
        setEtat({ statut: 'pret', message: '' });
      } catch (e) { if (!annule) setEtat({ statut: 'erreur', message: e.message }); }
    })();
    return () => { annule = true; };
  }, [cle]);

  const termesJackpot = () => jackpot.split(/[,;\n]/).map(x => x.trim()).filter(Boolean);
  const lancer = async () => {
    if (!etalon || !besoin?.id || lancement) return;
    setLancement(true);
    try {
      const r = await fetch(`${BASE}/sourcing/etalon/${etalon.id}/clone`, { method: 'POST', headers: jsonH(), body: JSON.stringify({
        besoin_id: besoin.id, query: query.trim() || undefined, ville: ville.trim() || undefined, jackpot: termesJackpot(), open_to_work: otwSeul || undefined,
        nombre: Math.max(1, Math.min(10, Number(nombre) || 1)),
        topN: Number(topN) || 5, engine: v2 ? 'v2' : undefined, mode: 'validate' }) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 405 || r.status === 501) { showNotification?.('La recherche de clones arrive côté serveur.', 'info'); return; }
      if (!r.ok || j.success === false) { showNotification?.(j.error || `Lancement impossible (HTTP ${r.status}).`, 'error'); return; }
      showNotification?.(j.message || `Recherche des clones de ${etalon.nom || 'ce profil'} lancée.`, 'success');
      onLance?.(j);
    } catch (e) { showNotification?.(`Lancement impossible : ${e.message}`, 'error'); }
    finally { setLancement(false); }
  };

  const d = etalon?.dossier || {};
  const liste = (a) => (Array.isArray(a) ? a.filter(Boolean) : []);
  const nom = etalon?.nom || cible?.nom || 'Profil';
  const provenance = { linkedin: 'profil LinkedIn extrait par le moteur', candidat: 'fiche candidat ERPIA', campagne: 'profil de la campagne', texte: 'texte fourni' }[etalon?.provenance] || null;

  return (
    <div className="pe-voile" onClick={onFermer} role="presentation">
      <style>{`
        .pe-voile { position: fixed; inset: 0; z-index: 1200; background: var(--erp-overlay, rgba(27, 42, 38, 0.42)); display: flex; justify-content: flex-end; }
        .pe-tiroir { width: min(600px, 100%); height: 100%; overflow-y: auto; background: var(--surface); border-left: 1px solid var(--border); display: flex; flex-direction: column; animation: pe-glisse 180ms ease-out both; }
        @keyframes pe-glisse { from { transform: translateX(24px); opacity: 0; } to { transform: none; opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .pe-tiroir { animation: none; } }
        .pe-tete { position: sticky; top: 0; z-index: 1; background: var(--surface); border-bottom: 1px solid var(--hair); padding: 14px 20px; display: flex; align-items: flex-start; gap: 12px; }
        .pe-kicker { font-family: 'Space Mono','IBM Plex Mono',monospace; font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: var(--text-3); }
        .pe-nom { font-size: 17px; font-weight: 800; letter-spacing: -0.02em; color: var(--text-strong); margin-top: 3px; line-height: 1.2; }
        .pe-titre { font-size: 12.5px; color: var(--text-2); margin-top: 3px; line-height: 1.4; }
        .pe-corps { padding: 6px 20px 24px; display: flex; flex-direction: column; gap: 18px; }
        .pe-bloc h4 { margin: 0 0 7px; font-family: 'Space Mono','IBM Plex Mono',monospace; font-size: 10px; letter-spacing: .12em; text-transform: uppercase; color: var(--text-3); font-weight: 600; }
        .pe-p { font-size: 13px; line-height: 1.55; color: var(--text); margin: 0; }
        .pe-liste { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 5px; }
        .pe-liste li { display: flex; gap: 8px; font-size: 12.5px; line-height: 1.45; color: var(--text-2); }
        .pe-liste li i { flex: none; margin-top: 3px; font-size: 11px; }
        .pe-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .pe-chip { font-size: 11.5px; font-weight: 600; padding: 3px 9px; border-radius: 6px; border: 1px solid var(--border); color: var(--text-2); background: var(--code-bg); }
        .pe-meta { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px 16px; }
        .pe-meta div span { display: block; font-family: 'Space Mono','IBM Plex Mono',monospace; font-size: 9.5px; letter-spacing: .1em; text-transform: uppercase; color: var(--text-3); }
        .pe-meta div b { font-size: 12.5px; font-weight: 600; color: var(--text-strong); }
        .pe-clone { border: 1px solid var(--border); border-radius: 11px; padding: 14px 16px; background: var(--bg); }
        .pe-champ { display: block; margin-top: 10px; }
        .pe-champ span { display: block; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: var(--text-3); margin-bottom: 5px; }
        .pe-champ input, .pe-champ textarea { width: 100%; box-sizing: border-box; border: 1px solid var(--input-border); border-radius: 8px; background: var(--input-bg); padding: 9px 11px; font-family: inherit; font-size: 12.5px; color: var(--text-strong); outline: none; }
        .pe-champ textarea { font-family: 'Space Mono','IBM Plex Mono',monospace; font-size: 11.5px; line-height: 1.5; min-height: 96px; resize: vertical; }
        .pe-champ input:focus-visible, .pe-champ textarea:focus-visible { border-color: var(--accent); }
        .pe-btn { display: inline-flex; align-items: center; gap: 7px; padding: 9px 14px; border-radius: 8px; border: 1px solid var(--input-border); background: var(--ghost-bg); color: var(--text); font-family: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; }
        .pe-btn:hover:not(:disabled) { border-color: var(--text-3); }
        .pe-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
        .pe-btn:disabled { opacity: .55; cursor: default; }
        .pe-btn--cta { background: var(--accent); color: var(--accent-fg); border-color: var(--accent); }
        .pe-fermer { margin-left: auto; width: 28px; height: 28px; border-radius: 7px; border: 1px solid var(--border); background: transparent; color: var(--text-3); cursor: pointer; display: flex; align-items: center; justify-content: center; flex: none; }
        .pe-fermer:hover { color: var(--text-strong); }
        .pe-attente { padding: 40px 20px; text-align: center; color: var(--text-2); font-size: 13px; line-height: 1.6; }
        .pe-attente i { display: inline-block; font-size: 22px; color: var(--accent); animation: pe-tour .9s linear infinite; margin-bottom: 10px; }
        @keyframes pe-tour { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) { .pe-attente i { animation: none; } }
        .pe-erreur { margin: 16px 20px; padding: 12px 14px; border-radius: 9px; border: 1px solid var(--danger-border, var(--border)); background: var(--danger-bg, var(--code-bg)); color: var(--danger-fg, var(--text)); font-size: 12.5px; line-height: 1.5; }
      `}</style>
      <aside className="pe-tiroir" role="dialog" aria-label={`Profil étalon — ${nom}`} onClick={(e) => e.stopPropagation()}>
        <div className="pe-tete">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pe-kicker">Profil étalon{provenance ? ` · ${provenance}` : ''}</div>
            <div className="pe-nom">{nom}</div>
            {(d.titre || cible?.titre) && <div className="pe-titre">{d.titre || cible.titre}</div>}
            {etat.statut === 'pret' && (
              <div className="pe-chips" style={{ marginTop: '8px' }}>
                {d.seniorite?.niveau && <span className="pe-chip">{NIVEAU[String(d.seniorite.niveau).toLowerCase()] || d.seniorite.niveau}{d.seniorite.annees != null ? ` · ~${Math.round(d.seniorite.annees)} ans` : ''}</span>}
                {villeUtile(d.localisation) && <span className="pe-chip"><BI icon="geo-alt" style={{ fontSize: '10px', marginRight: '4px' }} />{d.localisation}</span>}
                {liste(d.rares).length > 0 && <BadgeJackpot candidat={{ jackpot: d.rares }} />}
              </div>
            )}
          </div>
          <button type="button" className="pe-fermer" onClick={onFermer} aria-label="Fermer"><BI icon="x-lg" style={{ fontSize: '12px' }} /></button>
        </div>

        {etat.statut === 'lecture' && (
          <div className="pe-attente">
            <i className="bi bi-arrow-repeat" />
            <div>Lecture du profil en entier — une vingtaine de secondes.<br />Le moteur en tire le dossier, puis la recherche de ses clones.</div>
          </div>
        )}
        {etat.statut === 'absent' && (
          <div className="pe-erreur">L’analyse de profil arrive côté serveur. {etat.message}</div>
        )}
        {etat.statut === 'erreur' && (
          <div className="pe-erreur">{etat.message}</div>
        )}

        {etat.statut === 'pret' && (
          <div className="pe-corps">
            {d.resume && <div className="pe-bloc"><h4>Résumé</h4><p className="pe-p">{d.resume}</p></div>}
            {liste(d.points_forts).length > 0 && (
              <div className="pe-bloc"><h4>Points forts</h4>
                <ul className="pe-liste">{liste(d.points_forts).map((x, i) => <li key={i}><i className="bi bi-check-lg" style={{ color: 'var(--ok)' }} />{x}</li>)}</ul>
              </div>
            )}
            {liste(d.competences_cles).length > 0 && (
              <div className="pe-bloc"><h4>Compétences clés</h4>
                <div className="pe-chips">{liste(d.competences_cles).map((x, i) => <span key={i} className="pe-chip">{x}</span>)}</div>
              </div>
            )}
            {liste(d.rares).length > 0 && (
              <div className="pe-bloc"><h4>Mots rares — les jackpot de la chasse</h4>
                <div className="pe-chips">{liste(d.rares).map((x, i) => <BadgeJackpot key={i} candidat={{ jackpot: [x] }} />)}</div>
              </div>
            )}
            <div className="pe-bloc"><h4>Repères</h4>
              <div className="pe-meta">
                {liste(d.secteurs).length > 0 && <div><span>Secteurs</span><b>{liste(d.secteurs).join(', ')}</b></div>}
                {liste(d.langues).length > 0 && <div><span>Langues</span><b>{liste(d.langues).join(', ')}</b></div>}
                {d.mobilite && <div><span>Mobilité</span><b>{d.mobilite}</b></div>}
                {d.localisation && <div><span>Localisation</span><b>{d.localisation}</b></div>}
              </div>
            </div>
            {liste(d.signaux).length > 0 && (
              <div className="pe-bloc"><h4>Signaux</h4>
                <ul className="pe-liste">{liste(d.signaux).map((x, i) => <li key={i}><i className="bi bi-broadcast" style={{ color: 'var(--text-3)' }} />{x}</li>)}</ul>
              </div>
            )}
            {liste(d.a_verifier).length > 0 && (
              <div className="pe-bloc"><h4>À vérifier en entretien</h4>
                <ul className="pe-liste">{liste(d.a_verifier).map((x, i) => <li key={i}><i className="bi bi-question-circle" style={{ color: 'var(--warn)' }} />{x}</li>)}</ul>
              </div>
            )}

            <div className="pe-clone">
              <div className="pe-kicker" style={{ color: 'var(--text-strong)' }}><BI icon="people" style={{ marginRight: '6px' }} />Le clone</div>
              {liste(d.criteres_clone).length > 0 && (
                <ul className="pe-liste" style={{ marginTop: '9px' }}>{liste(d.criteres_clone).map((x, i) => <li key={i}><i className="bi bi-dot" />{x}</li>)}</ul>
              )}
              {etalon?.clone?.raisonnement && <p className="pe-p" style={{ marginTop: '9px', fontSize: '12px', color: 'var(--text-3)' }}>{etalon.clone.raisonnement}</p>}
              <label className="pe-champ"><span>Recherche dérivée — modifiable</span>
                <textarea value={query} onChange={e => setQuery(e.target.value)} spellCheck={false} />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <label className="pe-champ"><span>Zone visée</span><input value={ville} onChange={e => setVille(e.target.value)} placeholder="Genève" /></label>
                <label className="pe-champ"><span>Jackpot</span><input value={jackpot} onChange={e => setJackpot(e.target.value)} placeholder="Regdata, Avaloq" /></label>
              </div>
              <p className="pe-p">Les mots rares du jackpot sont <strong>cherchés en premier</strong> : la première passe exécute la requête ET l’un de ces mots (ils entrent dans la requête exécutée). Si elle ne suffit pas, la passe suivante reprend la requête complète, sans eux.</p>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', cursor: 'pointer' }}>
                <input type="checkbox" checked={otwSeul} onChange={e => setOtwSeul(e.target.checked)} style={{ marginTop: '3px' }} />
                <span className="pe-p">Uniquement les clones « open to work » — le réseau ciblé filtre la liste, le moteur écarte ce qui ne l’affiche pas, en disant pourquoi.</span>
              </label>
              {/* L'objectif : la campagne n'est réussie que si ce nombre de
                  profils COMME lui sort — pas quand N candidats passent un
                  seuil. Le moteur élargit tant que ce n'est pas le cas. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', padding: '10px 12px', borderRadius: '9px', border: '1px solid var(--border)', background: 'var(--surface)', flexWrap: 'wrap' }}>
                <span className="pe-kicker" style={{ color: 'var(--text-strong)' }}><BI icon="flag" style={{ marginRight: '6px', color: 'var(--erp-accent, var(--su-coral))' }} />Objectif</span>
                <span style={{ fontSize: '12.5px', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  trouver <input type="number" min={1} max={10} value={nombre} onChange={e => setNombre(e.target.value)} aria-label="Nombre de profils comme lui"
                    style={{ width: '52px', ...mono, border: '1px solid var(--input-border)', borderRadius: '7px', padding: '5px 7px', background: 'var(--input-bg)', color: 'var(--text-strong)' }} />
                  profil{Number(nombre) > 1 ? 's' : ''} comme <strong style={{ color: 'var(--text-strong)' }}>{nom}</strong>
                </span>
                <span style={{ flex: 1, minWidth: '160px', fontSize: '11.5px', color: 'var(--text-3)' }}>La campagne n’est réussie qu’une fois ce nombre sorti ; le moteur élargit jusque-là.</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginTop: '10px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', color: 'var(--text-2)' }}>
                  <input type="checkbox" checked={v2} onChange={e => setV2(e.target.checked)} /> Moteur par passes — élargit la zone tant qu’il ne trouve pas assez de clones
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12.5px', color: 'var(--text-2)' }}>
                  Top <input type="number" min={1} max={25} value={topN} onChange={e => setTopN(e.target.value)} style={{ width: '58px', ...mono, border: '1px solid var(--input-border)', borderRadius: '7px', padding: '5px 7px', background: 'var(--input-bg)', color: 'var(--text-strong)' }} />
                </label>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '14px', alignItems: 'center' }}>
                {besoin?.id && (
                  <button type="button" className="pe-btn pe-btn--cta" onClick={lancer} disabled={lancement || !query.trim()}>
                    <BI icon={lancement ? 'arrow-repeat' : 'people'} style={lancement ? { animation: 'pe-tour .8s linear infinite' } : undefined} /> Trouver son clone
                  </button>
                )}
                {onUtiliser && (
                  <button type="button" className="pe-btn" onClick={() => onUtiliser({ ...etalon, clone: { ...(etalon.clone || {}), query, ville, jackpot: termesJackpot() } })}>
                    <BI icon="pencil-square" /> Reprendre dans le formulaire
                  </button>
                )}
                {besoin?.titre && <span style={{ fontSize: '11.5px', color: 'var(--text-3)' }}>Campagne rattachée à « {besoin.titre} »</span>}
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
